/**
 * Skeleton — eDMS Design System (UX bolder-refresh, primitive #2)
 * Direction: docs/proposals/UX_REFRESH_DIRECTION.md §5 — "Loading is always a
 * skeleton, never a bare spinner."
 *
 * Exposes the base <Skeleton> block plus two content-shaped composites:
 *   <SkeletonText>  — N ragged paragraph lines
 *   <SkeletonCard>  — an elevated card placeholder (optional media + title + body)
 *
 * A11y: every node is aria-hidden (skeletons are decorative). The CONSUMER marks
 * the surrounding region aria-busy="true" while loading so a screen reader hears
 * "loading" once, then announces the resolved content — not one bar at a time.
 */

import { forwardRef, type CSSProperties, type HTMLAttributes } from 'react';
import { clsx } from 'clsx';
import styles from './Skeleton.module.css';

export type SkeletonVariant = 'rect' | 'text' | 'circle';

/** number → px, string → as-is (so callers can pass `120` or `'60%'`). */
const toCss = (v?: number | string): string | undefined =>
  typeof v === 'number' ? `${v}px` : v;

export interface SkeletonProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Shape: 'rect' (default), 'text' (short + baseline height), 'circle' (1:1). */
  variant?: SkeletonVariant;
  /** CSS width. Number → px. Defaults to 100% of the parent. */
  width?: number | string;
  /** CSS height. Number → px. Defaults per variant (text ≈ 0.85em). */
  height?: number | string;
  /** Border-radius override. Number → px. */
  radius?: number | string;
  /** Render a static block with no shimmer (long lists / low-power devices). */
  noAnimation?: boolean;
}

export const Skeleton = forwardRef<HTMLSpanElement, SkeletonProps>(function Skeleton(
  { variant = 'rect', width, height, radius, noAnimation = false, className, style, ...rest },
  ref,
) {
  const variantClass =
    variant === 'circle' ? styles.circle : variant === 'text' ? styles.text : styles.rect;
  const merged: CSSProperties = {
    width: toCss(width),
    height: toCss(height),
    borderRadius: toCss(radius),
    ...style,
  };
  return (
    <span
      ref={ref}
      aria-hidden="true"
      className={clsx(styles.skeleton, variantClass, noAnimation && styles.noAnim, className)}
      style={merged}
      {...rest}
    />
  );
});

export interface SkeletonTextProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Number of lines. Default 3. */
  lines?: number;
  /** Width of the final line, for a natural ragged edge. Default '60%'. Ignored when lines === 1. */
  lastLineWidth?: number | string;
  /** Per-line height override (number → px). Default: CSS 0.85em. */
  lineHeight?: number | string;
  /** Render static (no shimmer). */
  noAnimation?: boolean;
}

export function SkeletonText({
  lines = 3,
  lastLineWidth = '60%',
  lineHeight,
  noAnimation,
  className,
  ...rest
}: SkeletonTextProps) {
  const count = Math.max(1, Math.floor(lines));
  return (
    <span aria-hidden="true" className={clsx(styles.textBlock, className)} {...rest}>
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton
          key={i}
          variant="text"
          height={lineHeight}
          noAnimation={noAnimation}
          width={count > 1 && i === count - 1 ? lastLineWidth : '100%'}
        />
      ))}
    </span>
  );
}

export interface SkeletonCardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Leading circular media/avatar block. Default false. */
  media?: boolean;
  /** Body paragraph lines. Default 3. */
  lines?: number;
  /** Render static (no shimmer). */
  noAnimation?: boolean;
}

export function SkeletonCard({
  media = false,
  lines = 3,
  noAnimation,
  className,
  ...rest
}: SkeletonCardProps) {
  return (
    <div aria-hidden="true" className={clsx(styles.card, className)} {...rest}>
      {media && <Skeleton variant="circle" width={44} height={44} noAnimation={noAnimation} />}
      <div className={styles.cardBody}>
        <Skeleton variant="text" width="45%" height={16} noAnimation={noAnimation} />
        <SkeletonText lines={lines} noAnimation={noAnimation} />
      </div>
    </div>
  );
}
